import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { LocalNotifications } from '@capacitor/local-notifications'
import { StatusBar, Style } from '@capacitor/status-bar'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { Keyboard } from '@capacitor/keyboard'
import { App } from '@capacitor/app'

/**
 * Check if running on a native platform (iOS/Android)
 */
export const isNative = () => Capacitor.isNativePlatform()

/**
 * Check if running on iOS
 */
export const isIOS = () => Capacitor.getPlatform() === 'ios'

/**
 * Check if running on Android
 */
export const isAndroid = () => Capacitor.getPlatform() === 'android'

/**
 * Initialize native features when the app starts
 */
export async function initializeNativeFeatures() {
  if (!isNative()) {
    console.log('Running in web mode, skipping native initialization')
    return
  }

  try {
    // Configure status bar
    await configureStatusBar()

    // Setup keyboard listeners
    setupKeyboardListeners()

    // Setup app state listeners
    setupAppStateListeners()

    // Request push notification permission
    await requestPushNotificationPermission()

    console.log('Native features initialized successfully')
  } catch (error) {
    console.error('Error initializing native features:', error)
  }
}

/**
 * Configure status bar appearance
 */
async function configureStatusBar() {
  try {
    await StatusBar.setStyle({ style: Style.Light })

    if (isAndroid()) {
      await StatusBar.setBackgroundColor({ color: '#0f172a' })
    }
  } catch (error) {
    console.error('Error configuring status bar:', error)
  }
}

/**
 * Setup keyboard event listeners
 */
function setupKeyboardListeners() {
  Keyboard.addListener('keyboardWillShow', (info) => {
    document.body.classList.add('keyboard-open')
    document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`)
  })

  Keyboard.addListener('keyboardWillHide', () => {
    document.body.classList.remove('keyboard-open')
    document.documentElement.style.setProperty('--keyboard-height', '0px')
  })
}

/**
 * Setup app state change listeners
 */
function setupAppStateListeners() {
  App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      console.log('App became active')
      // Refresh data when app becomes active
    } else {
      console.log('App went to background')
    }
  })

  App.addListener('backButton', ({ canGoBack }) => {
    if (!canGoBack) {
      // Optionally show exit confirmation or minimize app
      App.minimizeApp()
    } else {
      window.history.back()
    }
  })
}

/**
 * Request push notification permission and register
 *
 * IMPORTANT: On Android, this requires Firebase to be configured with google-services.json
 * Without Firebase, calling PushNotifications.register() will crash the app.
 * Set PUSH_NOTIFICATIONS_ENABLED to true once Firebase is configured.
 */
const PUSH_NOTIFICATIONS_ENABLED = true // Set to true after adding google-services.json

export async function requestPushNotificationPermission(): Promise<string | null> {
  if (!isNative()) {
    // For web, use standard Notification API
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      return permission === 'granted' ? 'web-push-enabled' : null
    }
    return null
  }

  // Skip push notifications if not enabled (Firebase not configured)
  if (!PUSH_NOTIFICATIONS_ENABLED) {
    console.log('Push notifications disabled - Firebase not configured')
    return null
  }

  try {
    // Request permission
    const permStatus = await PushNotifications.checkPermissions()

    if (permStatus.receive === 'prompt') {
      const result = await PushNotifications.requestPermissions()
      if (result.receive !== 'granted') {
        console.log('Push notification permission denied')
        return null
      }
    } else if (permStatus.receive !== 'granted') {
      console.log('Push notification permission not granted')
      return null
    }

    // Register for push notifications
    return new Promise((resolve) => {
      PushNotifications.addListener('registration', (token) => {
        console.log('Push registration success, token:', token.value)
        resolve(token.value)
      })

      PushNotifications.addListener('registrationError', (err) => {
        console.error('Push registration failed:', err.error)
        resolve(null)
      })

      PushNotifications.register()
    })
  } catch (error) {
    console.error('Error setting up push notifications:', error)
    return null
  }
}

/**
 * Setup push notification listeners
 */
export function setupPushNotificationListeners(
  onReceived: (notification: any) => void,
  onActionPerformed: (action: any) => void
) {
  if (!isNative()) return

  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('Push notification received:', notification)
    onReceived(notification)
  })

  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('Push notification action:', action)
    onActionPerformed(action)
  })
}

/**
 * Schedule a local notification
 */
export async function scheduleLocalNotification(options: {
  title: string
  body: string
  id: number
  schedule?: {
    at?: Date
    repeats?: boolean
    every?: 'day' | 'hour' | 'minute'
  }
}) {
  try {
    // Check permission
    const permStatus = await LocalNotifications.checkPermissions()
    if (permStatus.display !== 'granted') {
      const result = await LocalNotifications.requestPermissions()
      if (result.display !== 'granted') {
        console.log('Local notification permission denied')
        return false
      }
    }

    await LocalNotifications.schedule({
      notifications: [
        {
          id: options.id,
          title: options.title,
          body: options.body,
          schedule: options.schedule,
          sound: 'default',
          smallIcon: 'ic_stat_icon',
          largeIcon: 'ic_launcher',
        },
      ],
    })

    return true
  } catch (error) {
    console.error('Error scheduling local notification:', error)
    return false
  }
}

/**
 * Cancel a scheduled local notification
 */
export async function cancelLocalNotification(id: number) {
  try {
    await LocalNotifications.cancel({
      notifications: [{ id }],
    })
    return true
  } catch (error) {
    console.error('Error canceling local notification:', error)
    return false
  }
}

/**
 * Trigger haptic feedback
 */
export async function triggerHapticFeedback(style: 'light' | 'medium' | 'heavy' = 'light') {
  if (!isNative()) return

  try {
    const impactStyle: Record<string, ImpactStyle> = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    }

    await Haptics.impact({ style: impactStyle[style] })
  } catch (error) {
    console.error('Error triggering haptic:', error)
  }
}

/**
 * Trigger haptic notification feedback
 */
export async function triggerNotificationHaptic(type: 'success' | 'warning' | 'error' = 'success') {
  if (!isNative()) return

  try {
    if (type === 'success') {
      await Haptics.notification({ type: 'SUCCESS' as any })
    } else if (type === 'warning') {
      await Haptics.notification({ type: 'WARNING' as any })
    } else {
      await Haptics.notification({ type: 'ERROR' as any })
    }
  } catch (error) {
    console.error('Error triggering notification haptic:', error)
  }
}

/**
 * Get app info
 */
export async function getAppInfo() {
  if (!isNative()) {
    return {
      name: 'Health Monitor',
      id: 'com.healthmonitor.app',
      build: '1',
      version: '1.0.0',
    }
  }

  try {
    return await App.getInfo()
  } catch (error) {
    console.error('Error getting app info:', error)
    return null
  }
}
